import { Badge, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { UserRole } from '@eppendorf/vnls-user-tenant-utils';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { useCurrentUserId } from '$shared/custom-hooks/useCurrentUserId';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { Loader } from '$components/loader/loader';
import { Overview } from '$components/overview-box/overview-box';
import { Tabs } from '$components/tabs/tabs';
import type { TabNavigationRoute } from '$components/tabs/tabs.types';
import { AuditReportList } from '$features/report-hub/audit-report/audit-report-list';
import { BetaInfoDialog } from '$features/report-hub/components/beta-info-dialog';
import { RunRecordList } from '$features/report-hub/run-record/run-record-list';
import { StabilityReportList } from '$features/report-hub/stability-report/stability-report-list';
import { useGetUser } from '$features/users/users.api';

import styles from './report-hub-list.module.scss';

const BETA_DIALOG_DISMISSED_KEY = 'vnls_report_hub_beta_dialog_dismissed';

export function ReportHubList(): ReactElement {
  const { t } = useTranslation();
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  const [dismissed, setDismissed] = useLocalStorage(BETA_DIALOG_DISMISSED_KEY, false);
  const [dialogOpen, setDialogOpen] = useState(!dismissed);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDismissed(true);
  };

  const handleBadgeClick = () => {
    setDialogOpen(true);
  };

  const { email: emailFromToken, tenantId } = useTokenAttributes(['email', 'tenantId']);
  const currentUserId = useCurrentUserId(emailFromToken, tenantId);
  const { data: currentUser, isLoading } = useGetUser(currentUserId || '');

  const activeTab = tab ?? 'stability';

  const isLabAdmin = currentUser?.roles?.includes(UserRole.LabAdmin);
  const isRunRecordsEnabled = useFeatureIsEnabled('ESEVNLS_4571_RUN_RECORDS');

  // Tab routes
  const tabRoutes: TabNavigationRoute[] = [
    {
      path: 'stability',
      label: t('reportHub.tabs.stability'),
    },
    ...(isRunRecordsEnabled
      ? [
          {
            path: 'run-records',
            label: t('reportHub.tabs.runRecords'),
          } as TabNavigationRoute,
        ]
      : []),
    {
      path: 'audit-trail',
      label: t('reportHub.tabs.auditTrail'),
    },
  ];

  const isActivePath = (path: string): boolean => activeTab === path;

  const handleTabClick = (path: string) => {
    navigate(`/report-hub/${path}`, { replace: true });
  };

  const renderAuditTrailContent = (): ReactElement => {
    if (isLoading) {
      return <Loader />;
    }
    if (!isLabAdmin) {
      return (
        <div className="p-l text-align-center" data-testid="report-hub-access-denied">
          {t('reportHub.accessDenied')}
        </div>
      );
    }
    return <AuditReportList />;
  };

  return (
    <>
      <BetaInfoDialog isOpen={dialogOpen} onClose={handleDialogClose} />
      <Overview.Header>
        <div className={styles.reportHubListHeaderInner}>
          <div className={styles.reportHubListHeadline}>
            <h1 className="title">{t('reportHub.title')}</h1>
            <button type="button" className="cursor--pointer" onClick={handleBadgeClick}>
              <Badge
                color="purple"
                size="small"
                className={`flex flex__ai--center gap-xs bg-purple-500 color-white font-weight-bold ${styles.betaBadge}`}
              >
                <Icon name="flag" size={IconSizeClasses.XXSmall} className="bg-white" />
                {t('reportHub.beta.badge')}
              </Badge>
            </button>
          </div>
          <Tabs
            routes={tabRoutes}
            isActivePath={isActivePath}
            handleTabClick={handleTabClick}
          />
        </div>
      </Overview.Header>

      <Overview.Body>
        {activeTab === 'audit-trail' && renderAuditTrailContent()}
        {activeTab === 'run-records' && <RunRecordList />}
        {activeTab === 'stability' && <StabilityReportList />}
      </Overview.Body>
    </>
  );
}
